<template>
    <div class="kv-table-footer gap-xl" v-if="total > 0">
        <div class="kv-table-items-per-page d-flex align-items-center gap-md flex-wrap">
            <div class="kv-table-info" v-if="showInfo">
                {{ t('pagination-showing', [start, end, total]) }}
            </div>
            <div v-if="showItemsPerPage">
                {{ t('pagination-items_per_page') }}
                <kv-select :value="itemsPerPage" @input="$emit('items-per-page-change', $event)" :options="perPageOptionsFormatted" maxContent/>
            </div>
        </div>

        <div class="kv-pagination" v-if="pages > 1">
            <div class="outlined-container">
                <span class="page kv-before" @click="goToPrev" :class="{ 'kv-disabled': currentPage === 1 }">
                    <Icon icon-class="ui-chevron-left" size="4" />
                </span>
            </div>

            <div class="outlined-container">
                <span v-for="(page, index) in pagesArray" :key="page.label + index">
                    <span v-if="page.page" class="page" :class="{ active: page.page === currentPage }" @click="goToPage(page.page)">
                        {{ page.label }}
                    </span>

                    <KvPopup placement="top left" v-else @after-open="() => focusGotoInput(index)">
                        <template #trigger="{ toggle }">
                            <span class="page" @click.stop="toggle()">...</span>
                        </template>

                        <template #content="{ toggle }">
                            <div class="popup-page-input">
                                <span>{{ t('pagination-go_to') }} </span>
                                <!-- i18n-validator-ignore-next-line: numeric placeholder for page input -->
                                <KvInput :ref="'gotoInput-' + index" :validator="validator" always-validate v-model.number="gotoPage" type="number" :max="pages" placeholder="1" @keydown.enter="handleEnter(toggle)" />
                            </div>
                        </template>
                    </KvPopup>
                </span>
            </div>

            <div class="outlined-container">
                <span class="page kv-next" @click="goToNext" :class="{ 'kv-disabled': currentPage === pages }">
                    <Icon icon-class="ui-chevron-right" size="4" />
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import Icon from '../icon/icon.vue';
import KvInput from '../inputs/kv-input.vue';
import KvSelect from '../inputs/kv-select.vue';
import KvPopup from './kv-popup.vue';

export default {
    name: 'kv-pagination',
    components: {
        Icon,
        KvInput,
        KvSelect,
        KvPopup
    },
    props: {
        total: {
            type: [String, Number],
            required: true
        },
        currentPage: {
            type: Number,
            required: true
        },
        itemsPerPage: {
            type: Number,
            required: true
        },
        showItemsPerPage: {
            type: Boolean,
            default: false
        },
        showInfo: {
            type: Boolean,
            default: true
        },
        perPageOptions: {
            type: Array,
            default: () => [10, 20, 50, 100]
        }
    },
    data() {
        return {
            gotoPage: null,
            isMobile: false,
        };
    },
    mounted() {
        this.checkMobile();
        window.addEventListener('resize', this.checkMobile);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.checkMobile);
    },
    computed: {
        pages() {
            return Math.ceil(this.total / this.itemsPerPage);
        },
        start() {
            return (this.currentPage - 1) * this.itemsPerPage + 1;
        },
        end() {
            return Math.min(this.currentPage * this.itemsPerPage, this.total);
        },
        perPageOptionsFormatted() {
            return this.perPageOptions.map(value => ({
                label: String(value),
                value: value
            }));
        },
        pagesArray() {
            const maxVisible = this.isMobile ? 3 : 5;
            const offset = Math.floor(maxVisible / 2);

            let pages = [];

            if (this.pages < 10) {
                for (let i = 1; i <= this.pages; i++) {
                    pages.push({ label: i, page: i, separator: null });
                }
            } else {
                let currentPage = this.currentPage;

                // Gera um range seguro ao redor da página atual
                for (let i = currentPage - maxVisible; i <= currentPage + maxVisible; i++) {
                    pages.push({ label: i, page: i, separator: null });
                }
                pages = pages.filter((i) => i.page > 0 && i.page <= this.pages);
                let startSlide = 0;
                let currentPageIndex = pages.findIndex((i) => i.page === currentPage);
                startSlide = currentPageIndex - offset;
                if (startSlide < 0) {
                    startSlide = 0;
                }
                if (startSlide + maxVisible > pages.length) {
                    startSlide = pages.length - maxVisible;
                }
                pages = pages.slice(startSlide, startSlide + maxVisible);
                if (pages[0].page > 1) {
                    pages = [
                        { label: 1, page: 1, separator: null },
                        { label: "...", page: null, separator: "after" },
                        ...pages,
                    ];
                }
                if (pages[pages.length - 1].page < this.pages) {
                    pages = [
                        ...pages,
                        { label: "...", page: null, separator: "before" },
                        { label: this.pages, page: this.pages, separator: null },
                    ];
                }
            }
            return pages;
        }
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        checkMobile() {
            this.isMobile = window.innerWidth <= 600;
        },
        goToPage(page) {
            if (page > 0 && page <= this.pages && page !== this.currentPage) {
                this.$emit('page-change', page);
            }
        },
        goToPrev() {
            this.goToPage(this.currentPage - 1);
        },
        goToNext() {
            this.goToPage(this.currentPage + 1);
        },
        handleEnter(toggle) {
            if (this.validator(this.gotoPage)) {
                this.goToPage(this.gotoPage);
                toggle(false);
                this.gotoPage = null;
            }
        },
        focusGotoInput(index) {
            this.$nextTick(() => {
                const ref = this.$refs['gotoInput-' + index];
                if (!ref) return;
                const input = Array.isArray(ref) ? ref[0] : ref;
                if (input && typeof input.focus === 'function') {
                    input.focus();
                }
            });
        },
        validator(value) {
            return value === '' || value === null || (value > 0 && value <= this.pages);
        }
    }
};
</script>

<style lang="scss" scoped>
.kv-table-footer {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    align-items: center;
    flex-wrap: wrap;
    min-height: 47px;

    .kv-table-items-per-page {
        font-size: 14px;
    }

    .kv-table-info {
        color: #9DA4AE;
        font-weight: 500;
    }
}

.kv-pagination {
    display: flex;
    justify-content: flex-end;
    gap: 5px;

    .popup-page-input {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .outlined-container {
        display: flex;
        border: 1px solid #F3F4F6;
        align-items: center;
        padding: 5px;
        border-radius: 8px;
        gap: 5px;
    }

    .page {
        color: #888;
        font-weight: 500;
        width: 35px;
        height: 35px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        box-sizing: border-box;
        border-radius: 8px;
        cursor: pointer;
        transition: all ease-in-out 0.3s;

        &:hover {
            background-color: #F1FCF3;
        }

        &.active {
            background-color: #2AAA4C;
            color: #FFF;
        }
    }

    .kv-disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    // Mobile adjustments
    @media (max-width: 600px) {
        justify-content: center;
        gap: 2px;

        .outlined-container {
            padding: 3px;
            gap: 2px;
        }

        .page {
            width: 26px;
            height: 26px;
            font-size: 12px;
            border-radius: 6px;
        }

        .kv-before,
        .kv-next {
            width: 28px;
            height: 28px;
        }
    }
}
</style>
